"use client";

import { c as _c } from "react/compiler-runtime";
import { startTransition, Suspense, useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { CalendarDays, CheckCircle2, Clock, Globe } from "lucide-react";
import { Section } from "@/components/section";
import { Card } from "@/components/ui/card";
import { SeoHeading } from "@/components/common/seo-heading";
import { Text } from "@/components/ui/text";
import { Heading } from "@/components/ui/heading";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "@/components/ui/empty";
import { TimeSlotPicker } from "@/components/common/time-slot-picker";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useMounted } from "@/hooks/use-mounted";
import dynamic from "next/dynamic";
const ScheduleAnAppoimentForm = dynamic(() => import("@/components/forms/schedule-an-appoinment-form").then(mod => mod.ScheduleAnAppoimentForm), {
  ssr: false
});
type KnowBenefitsOfSpoclearnProps = {
  section: PageSections;
  data: {
    serviceId: string;
    seoHeading?: string;
    heading?: string;
    description?: string;
    buttonText?: string;
  };
};
const KnowBenefitsOfSpoclearn = t0 => {
  const $ = _c(53);
  const {
    section,
    data
  } = t0;
  const mounted = useMounted();
  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [timezone, setTimezone] = useState(null);
  let d;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    $[0] = d;
  } else {
    d = $[0];
  }
  const tomorrow = d;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      startTransition(() => {
        try {
          setTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        } catch {
          setTimezone("your local timezone");
        }
      });
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const open = time !== "";
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = isOpen => {
      if (!isOpen) {
        setTime("");
      }
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleOpenChange = t3;
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="bg-primary/5 pointer-events-none absolute -top-20 -left-20 h-40 w-40 rounded-full blur-3xl" />;
    t5 = <div className="bg-secondary/5 pointer-events-none absolute -right-20 -bottom-20 h-40 w-40 rounded-full blur-3xl" />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Badge className="border-primary/5 bg-primary/10 text-primary mb-6 border text-xs font-bold tracking-wider uppercase"><CalendarDays size={12} className="text-primary" /><span>1-on-1 Advisory</span></Badge>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = data.heading || "Let\u2019s Talk \u2013 Schedule a Time That Works for You";
  let t8;
  if ($[7] !== data.seoHeading || $[8] !== t7) {
    t8 = <SeoHeading as="h2" seoHeading={data.seoHeading} heading={t7} />;
    $[7] = data.seoHeading;
    $[8] = t7;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const t9 = data.description || "We know your time matters. Use the calendar below to book a\n                consultation with our team at your convenience\u2014adjusted to your\n                timezone. Let\u2019s explore how Spoclearn can help you transform and\n                grow.";
  let t10;
  if ($[10] !== t9) {
    t10 = <Text className="mb-8 leading-relaxed">{t9}</Text>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="space-y-4">{[{
        title: "Personalized Strategy",
        desc: "Discuss learning paths tailored for your team's goals."
      }, {
        title: "Expert Insights",
        desc: "Direct answers to your curriculum and delivery questions."
      }, {
        title: "No-Obligation Session",
        desc: "A completely free 15-minute diagnostic consultation."
      }].map(_temp)}</div>;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t8) {
    t12 = <div>{t6}{t8}{t10}{t11}</div>;
    $[13] = t10;
    $[14] = t8;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="text-primary flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-50"><Globe size={16} className="text-primary animate-pulse" /></div>;
    t14 = <Text className="mb-1 text-[10px] leading-none font-bold tracking-wider uppercase">Your Timezone</Text>;
    $[16] = t13;
    $[17] = t14;
  } else {
    t13 = $[16];
    t14 = $[17];
  }
  const t15 = timezone ?? "Loading...";
  let t16;
  if ($[18] !== t15) {
    t16 = <div className="bg-background mt-8 flex items-center gap-3 rounded-2xl p-4 shadow-sm sm:mt-10">{t13}<div>{t14}<Text className="text-muted-foreground text-xs leading-none font-semibold">{t15}</Text></div></div>;
    $[18] = t15;
    $[19] = t16;
  } else {
    t16 = $[19];
  }
  let t17;
  if ($[20] !== t12 || $[21] !== t16) {
    t17 = <div className="border-border col-span-12 flex flex-col justify-between border-b p-6 sm:col-span-7 sm:p-10 md:border-r md:border-b-0 lg:p-12">{t12}{t16}</div>;
    $[20] = t12;
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  let t18;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <div className="mb-3 flex items-center gap-2"><div className="bg-primary/5 text-primary flex h-7 w-7 items-center justify-center rounded-lg"><CalendarDays size={14} /></div><Heading as="h3" className="text-base font-bold sm:text-lg">1. Scheduled Date</Heading></div>;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== date || $[25] !== mounted) {
    t19 = <div className="flex flex-col">{t18}{mounted ? <Suspense fallback={<p>Loading</p>}><Calendar mode="single" selected={date} onSelect={setDate} className="mx-auto mb-3 w-full rounded-2xl border [&_tbody_td]:aspect-2/1 [&_tbody_td_button]:aspect-2/1" captionLayout="dropdown" disabled={tomorrow ? {
          before: tomorrow
        } : undefined} /></Suspense> : <div className="bg-muted/20 mx-auto mb-3 h-65.75 w-full animate-pulse rounded-2xl border" />}</div>;
    $[24] = date;
    $[25] = mounted;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  let t20;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="mb-3 flex items-center gap-2"><div className="bg-primary/5 text-primary flex h-7 w-7 items-center justify-center rounded-lg"><Clock size={14} /></div><Heading as="h3" className="text-base font-bold sm:text-lg">2. Scheduled Time</Heading></div>;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== date || $[29] !== mounted || $[30] !== time) {
    t21 = <div className="flex flex-col justify-stretch">{t20}{!mounted ? <div className="bg-muted/20 h-43 w-full animate-pulse rounded-2xl border" /> : !date ? <Empty className="border border-dashed"><EmptyHeader><EmptyMedia variant="icon"><Clock /></EmptyMedia><EmptyTitle>Awaiting Date Selection</EmptyTitle><EmptyDescription>Please pick a date on the calendar above to view available consulting slots.</EmptyDescription></EmptyHeader></Empty> : <TimeSlotPicker selectedDate={date} selectedTime={time} onTimeSelect={newTime => setTime(newTime)} hideHeading={true} className="bg-background [&_.mb-4]:mb-2! [&_.space-y-8]:space-y-4! [&_h4]:text-sm! [&_svg]:size-5!" />}</div>;
    $[28] = date;
    $[29] = mounted;
    $[30] = time;
    $[31] = t21;
  } else {
    t21 = $[31];
  }
  let t22;
  if ($[32] !== t19 || $[33] !== t21) {
    t22 = <div className="col-span-12 flex flex-col justify-start p-6 sm:col-span-5 sm:p-10 lg:p-12"><div className="h-full">{t19}{t21}</div></div>;
    $[32] = t19;
    $[33] = t21;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t17 || $[36] !== t22) {
    t23 = <Card className="relative w-full overflow-hidden p-0">{t4}{t5}<div className="relative z-10 grid grid-cols-12">{t17}{t22}</div></Card>;
    $[35] = t17;
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] !== date) {
    t24 = date instanceof Date ? format(date, "yyyy-MM-dd") : "";
    $[38] = date;
    $[39] = t24;
  } else {
    t24 = $[39];
  }
  const t25 = data?.serviceId;
  let t26;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = () => {
      setTime("");
      setDate(undefined);
    };
    $[40] = t26;
  } else {
    t26 = $[40];
  }
  let t27;
  if ($[41] !== t24 || $[42] !== t25 || $[43] !== time) {
    t27 = <DialogContent className="sm:max-w-md"><Suspense><ScheduleAnAppoimentForm date={t24} time={time} serviceId={t25} onSuccess={t26} /></Suspense></DialogContent>;
    $[41] = t24;
    $[42] = t25;
    $[43] = time;
    $[44] = t27;
  } else {
    t27 = $[44];
  }
  let t28;
  if ($[45] !== open || $[46] !== t27) {
    t28 = <Dialog open={open} onOpenChange={handleOpenChange}>{t27}</Dialog>;
    $[45] = open;
    $[46] = t27;
    $[47] = t28;
  } else {
    t28 = $[47];
  }
  let t29;
  if ($[48] !== section.sectionClass || $[49] !== section.sectionId || $[50] !== t23 || $[51] !== t28) {
    t29 = <Section id={section.sectionId} className={section.sectionClass}>{t23}{t28}</Section>;
    $[48] = section.sectionClass;
    $[49] = section.sectionId;
    $[50] = t23;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  return t29;
};
export default KnowBenefitsOfSpoclearn;
function _temp(benefit, idx) {
  return <div key={idx} className="flex items-start gap-3.5"><div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 size={14} className="stroke-[2.5]" /></div><div><Text className="mb-0.5 text-sm leading-none font-semibold">{benefit.title}</Text><Text className="text-muted-foreground text-xs leading-normal">{benefit.desc}</Text></div></div>;
}